import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, X, ShieldAlert, Trash2, PlayCircle, PlugZap } from "lucide-react";
import { ApiError } from "@finora/api-client";
import type { BackupRestoreRequestDto } from "@finora/api-client";
import type { BackupDestinationDto, BackupDestinationPayload, BackupDestinationType } from "@finora/types";
import { EmptyState, Skeleton, Button } from "@finora/ui";
import { api } from "../../lib/api";

const TYPE_LABELS: Record<BackupDestinationType, string> = {
  LOCAL: "Lokalni disk servera",
  SFTP: "SFTP",
  FTP: "FTP",
  S3: "S3 / S3-kompatibilno",
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  PENDING: { label: "Na čekanju", className: "text-slate-500" },
  DUMPING: { label: "Pravi se dump…", className: "text-amber-600" },
  UPLOADING: { label: "Otprema se…", className: "text-amber-600" },
  VERIFYING: { label: "Proverava se…", className: "text-amber-600" },
  SUCCESS: { label: "Uspešno", className: "text-emerald-600" },
  FAILED: { label: "Neuspešno", className: "text-red-600" },
};

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DestinationFormModal({
  initial,
  onClose,
}: {
  initial: BackupDestinationDto | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<BackupDestinationType>(initial?.type ?? "LOCAL");
  const [host, setHost] = useState(initial?.host ?? "");
  const [port, setPort] = useState(initial?.port ? String(initial.port) : "");
  const [remotePath, setRemotePath] = useState(initial?.remotePath ?? "");
  const [username, setUsername] = useState(initial?.username ?? "");
  const [secret, setSecret] = useState("");
  const [bucket, setBucket] = useState(initial?.bucket ?? "");
  const [region, setRegion] = useState(initial?.region ?? "");
  const [endpoint, setEndpoint] = useState(initial?.endpoint ?? "");
  const [cronExpression, setCronExpression] = useState(initial?.cronExpression ?? "0 2 * * *");
  const [retentionCount, setRetentionCount] = useState(String(initial?.retentionCount ?? 7));

  const save = useMutation({
    mutationFn: () => {
      const payload: BackupDestinationPayload = {
        name,
        type,
        host: host || null,
        port: port ? Number(port) : null,
        remotePath: remotePath || null,
        username: username || null,
        bucket: bucket || null,
        region: region || null,
        endpoint: endpoint || null,
        cronExpression,
        retentionCount: Number(retentionCount) || 7,
        ...(secret ? { secret } : {}),
      };
      return initial
        ? api.updateBackupDestination(initial.id, payload)
        : api.createBackupDestination(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backup-destinations"] });
      onClose();
    },
  });

  const needsHost = type === "SFTP" || type === "FTP";
  const needsBucket = type === "S3";
  const canSubmit = name && cronExpression && (type === "LOCAL" ? remotePath : true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {initial ? "Izmeni odredište" : "Novo odredište bekapa"}
          </h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Naziv</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Tip</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as BackupDestinationType)}
              disabled={Boolean(initial)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none disabled:bg-slate-50"
            >
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {type === "LOCAL" && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Putanja na serveru</label>
              <input
                value={remotePath}
                onChange={(e) => setRemotePath(e.target.value)}
                placeholder="/opt/finora/backups"
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          )}

          {needsHost && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700">Host</label>
                  <input
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Port</label>
                  <input
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder={type === "SFTP" ? "22" : "21"}
                    className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Udaljena putanja</label>
                <input
                  value={remotePath}
                  onChange={(e) => setRemotePath(e.target.value)}
                  placeholder="/backup/finora"
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </>
          )}

          {needsBucket && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Bucket</label>
                  <input
                    value={bucket}
                    onChange={(e) => setBucket(e.target.value)}
                    className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Region</label>
                  <input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="us-east-1"
                    className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Endpoint (S3-kompatibilno - MinIO/DO Spaces/B2/Wasabi, prazno za pravi AWS)
                </label>
                <input
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Putanja/prefiks (opciono)</label>
                <input
                  value={remotePath}
                  onChange={(e) => setRemotePath(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            </>
          )}

          {(needsHost || needsBucket) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  {needsBucket ? "Access Key ID" : "Korisničko ime"}
                </label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  {needsBucket ? "Secret Access Key" : type === "SFTP" ? "Lozinka / SSH ključ" : "Lozinka"}
                  {initial?.hasSecret && !secret && (
                    <span className="ml-1 font-normal text-slate-400">(već podešeno, ostavi prazno da zadržiš)</span>
                  )}
                </label>
                {type === "SFTP" ? (
                  <textarea
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    placeholder="Lozinka ili privatni SSH ključ (-----BEGIN ...)"
                    rows={secret.includes("\n") ? 4 : 1}
                    className="w-full rounded-lg border border-border px-3 py-2 font-mono text-xs focus:border-brand focus:outline-none"
                  />
                ) : (
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Cron raspored</label>
              <input
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                placeholder="0 2 * * *"
                className="h-10 w-full rounded-lg border border-border px-3 text-sm font-mono focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Zadrži poslednjih N validnih
              </label>
              <input
                value={retentionCount}
                onChange={(e) => setRetentionCount(e.target.value)}
                className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>
          <p className="rounded-lg bg-app-bg px-3 py-2 text-xs text-slate-500">
            Retencija ovde samo OBELEŽAVA stare bekape kao "kandidate za brisanje" u istoriji ispod - ništa se
            ne briše automatski. Brisanje sa udaljenog odredišta zahteva eksplicitan klik na svaki bekap
            pojedinačno, i sistem uvek odbija da obriše poslednji preostali validan bekap.
          </p>

          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button className="flex-1" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

function DestinationCard({
  destination,
  selected,
  onToggleSelected,
}: {
  destination: BackupDestinationDto;
  selected: boolean;
  onToggleSelected: (id: string, checked: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [confirmDeleteRunId, setConfirmDeleteRunId] = useState<string | null>(null);
  const [showAllRuns, setShowAllRuns] = useState(false);

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ["backup-runs", destination.id],
    queryFn: () => api.listBackupRuns(destination.id),
    refetchInterval: 5000,
  });

  const test = useMutation({
    mutationFn: () => api.testBackupDestination(destination.id),
    onSuccess: () => setTestResult("Konekcija je uspešna."),
    onError: (err) => setTestResult(err instanceof ApiError ? err.response.message : "Test nije uspeo."),
  });

  const runNow = useMutation({
    mutationFn: () => api.runBackupNow(destination.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["backup-runs", destination.id] }),
  });

  const deleteDestination = useMutation({
    mutationFn: () => api.deleteBackupDestination(destination.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["backup-destinations"] }),
  });

  const deleteRun = useMutation({
    mutationFn: (runId: string) => api.deleteBackupRun(runId),
    onSuccess: () => {
      setConfirmDeleteRunId(null);
      queryClient.invalidateQueries({ queryKey: ["backup-runs", destination.id] });
    },
    onError: () => setConfirmDeleteRunId(null),
  });

  return (
    <div className="rounded-xl border border-border bg-surface shadow-subtle">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onToggleSelected(destination.id, e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border text-brand focus:ring-brand"
            aria-label={`Izaberi odredište ${destination.name}`}
          />
          <div>
            <p className="font-medium text-slate-900">
              {destination.name}{" "}
              <span className="ml-1 text-xs font-normal text-slate-400">({TYPE_LABELS[destination.type]})</span>
            </p>
            <p className="font-mono text-xs text-slate-400">
              {destination.cronExpression} · zadrži {destination.retentionCount}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
            <PlugZap className="h-3.5 w-3.5" aria-hidden />
            Testiraj
          </Button>
          <Button size="sm" variant="secondary" onClick={() => runNow.mutate()} disabled={runNow.isPending}>
            <PlayCircle className="h-3.5 w-3.5" aria-hidden />
            Pokreni sada
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Izmeni
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (confirm(`Obrisati odredište "${destination.name}" i celu istoriju bekapa uz njega?`)) {
                deleteDestination.mutate();
              }
            }}
          >
            Obriši
          </Button>
        </div>
      </div>

      {testResult && (
        <p className="border-b border-border px-5 py-2 text-xs text-slate-500">{testResult}</p>
      )}

      <div className="px-5 py-4">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Istorija</p>
        {runsLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !runs || runs.length === 0 ? (
          <p className="text-sm text-slate-400">Još nema bekapa za ovo odredište.</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400">
                  <th className="pb-2 pr-3">Kada</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3">Veličina</th>
                  <th className="pb-2 pr-3">Restorabilnost</th>
                  <th className="pb-2 pr-3">Trajanje</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {(showAllRuns ? runs : runs.slice(0, 10)).map((run) => {
                  const statusInfo = STATUS_LABELS[run.status] ?? { label: run.status, className: "" };
                  return (
                    <tr key={run.id} className="border-t border-border">
                      <td className="py-2 pr-3 text-slate-600">
                        {new Date(run.startedAt).toLocaleString("sr-RS")}
                        {run.trigger === "MANUAL" && (
                          <span className="ml-1 text-xs text-slate-400">(ručno)</span>
                        )}
                      </td>
                      <td className={`py-2 pr-3 font-medium ${statusInfo.className}`}>
                        {statusInfo.label}
                        {run.retentionCandidate && (
                          <span
                            title="Van retencionog praga - kandidat za brisanje, ali NIJE obrisan automatski"
                            className="ml-1 inline-flex items-center text-amber-500"
                          >
                            <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
                          </span>
                        )}
                        {run.errorMessage && (
                          <p className="mt-0.5 max-w-xs text-xs font-normal text-red-500">{run.errorMessage}</p>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-slate-500">{formatBytes(run.sizeBytes)}</td>
                      <td className="py-2 pr-3">
                        {run.restorabilityCheckOk === null ? (
                          <span className="text-slate-400">—</span>
                        ) : run.restorabilityCheckOk ? (
                          <span className="text-emerald-600">Potvrđena</span>
                        ) : (
                          <span className="text-red-600">Nije prošla</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-slate-500">
                        {run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : "—"}
                      </td>
                      <td className="py-2 text-right">
                        {run.deletedAt ? (
                          <span className="text-xs text-slate-400">obrisan</span>
                        ) : run.status === "SUCCESS" ? (
                          confirmDeleteRunId === run.id ? (
                            <span className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="danger"
                                disabled={deleteRun.isPending}
                                onClick={() => deleteRun.mutate(run.id)}
                              >
                                Potvrdi
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => setConfirmDeleteRunId(null)}>
                                Otkaži
                              </Button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteRunId(run.id)}
                              className="text-slate-400 hover:text-red-600"
                              title="Obriši ovaj bekap sa udaljenog odredišta"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </button>
                          )
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {runs.length > 10 && (
              <button
                onClick={() => setShowAllRuns((v) => !v)}
                className="mt-2 text-sm text-brand hover:underline"
              >
                {showAllRuns ? "Prikaži samo poslednjih 10" : `Prikaži sve (${runs.length})`}
              </button>
            )}
          </div>
        )}
      </div>

      {editing && <DestinationFormModal initial={destination} onClose={() => setEditing(false)} />}
    </div>
  );
}


const RESTORE_STATUS: Record<string, { label: string; className: string }> = {
  QUEUED: { label: "Na čekanju…", className: "text-amber-600" },
  RUNNING: { label: "Radi se…", className: "text-amber-600" },
  DONE: { label: "Završeno", className: "text-emerald-600" },
  FAILED: { label: "Neuspešno", className: "text-red-600" },
};

function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function RestorePanel() {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState("");
  const [when, setWhen] = useState(localInputValue(new Date(Date.now() - 60 * 60 * 1000)));
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const companies = useQuery({ queryKey: ["admin-companies-restore"], queryFn: () => api.listAllCompanies() });
  const requests = useQuery({
    queryKey: ["backup-restores"],
    queryFn: () => api.listBackupRestores(),
    refetchInterval: (q) =>
      (q.state.data as BackupRestoreRequestDto[] | undefined)?.some((r) => r.status === "QUEUED" || r.status === "RUNNING")
        ? 4000
        : false,
  });

  const create = useMutation({
    mutationFn: (mode: "PREVIEW" | "APPLY") =>
      api.createBackupRestore({ companyId, targetTime: new Date(when).toISOString(), mode }),
    onSuccess: (r) => {
      setError(null);
      setConfirmText("");
      setOpenId(r.id);
      queryClient.invalidateQueries({ queryKey: ["backup-restores"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Greška pri slanju zahteva."),
  });

  const company = companies.data?.find((c) => c.id === companyId);
  const iso = when ? new Date(when).toISOString() : "";
  const previewDone = requests.data?.find(
    (r) => r.mode === "PREVIEW" && r.status === "DONE" && r.companyId === companyId && r.targetTime === iso,
  );
  const busy = requests.data?.some((r) => r.status === "QUEUED" || r.status === "RUNNING");

  return (
    <div className="space-y-3 rounded-xl border border-border bg-surface p-5">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Vraćanje jedne firme na izabrano vreme</h2>
        <p className="text-sm text-slate-500">
          Vraća samo podatke izabrane firme (ostale se ne diraju). Koristi pgBackRest, pa radi samo za vreme koje bekap
          pokriva. Sve što je firma radila posle izabranog vremena se gubi. Stanje pre vraćanja se sačuva u fajl na serveru.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="h-10 rounded-lg border border-border px-3 text-sm"
        >
          <option value="">Izaberi firmu…</option>
          {(companies.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="h-10 rounded-lg border border-border px-3 text-sm"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => create.mutate("PREVIEW")} disabled={!companyId || !when || busy || create.isPending}>
          Pregled (ništa se ne menja)
        </Button>
        {previewDone && (
          <>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Ukucaj "${company?.name ?? ""}" za potvrdu`}
              className="h-9 w-72 rounded-lg border border-border px-3 text-sm"
            />
            <Button
              size="sm"
              onClick={() => create.mutate("APPLY")}
              disabled={confirmText !== company?.name || busy || create.isPending}
            >
              Vrati firmu
            </Button>
          </>
        )}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {requests.data && requests.data.length > 0 && (
        <div className="divide-y divide-border rounded-lg border border-border">
          {requests.data.slice(0, 8).map((r) => {
            const st = RESTORE_STATUS[r.status] ?? { label: r.status, className: "" };
            return (
              <div key={r.id} className="px-3 py-2 text-sm">
                <button className="flex w-full flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                  <span>
                    {r.mode === "APPLY" ? "Vraćanje" : "Pregled"} · {r.companyName} · {new Date(r.targetTime).toLocaleString("sr-RS")}
                  </span>
                  <span className={st.className}>{st.label}</span>
                </button>
                {openId === r.id && r.output && (
                  <pre className="mt-2 max-h-72 overflow-auto rounded bg-slate-50 p-2 text-xs text-slate-700">{r.output}</pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BackupPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkRunResult, setBulkRunResult] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["backup-destinations"],
    queryFn: () => api.listBackupDestinations(),
  });

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const destinationIds = data?.destinations.map((d) => d.id) ?? [];
  const allSelected = destinationIds.length > 0 && destinationIds.every((id) => selectedIds.includes(id));

  const runSelected = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(selectedIds.map((id) => api.runBackupNow(id)));
      return results;
    },
    onSuccess: (results) => {
      const failed = results.filter((r) => r.status === "rejected").length;
      const ok = results.length - failed;
      setBulkRunResult(
        failed === 0
          ? `Bekap pokrenut za svih ${ok} izabranih odredišta.`
          : `Pokrenuto za ${ok} odredišta, ${failed} nije uspelo da se pokrene.`,
      );
      selectedIds.forEach((id) => queryClient.invalidateQueries({ queryKey: ["backup-runs", id] }));
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Bekap baze</h1>
          <p className="text-sm text-slate-500">
            Odredišta i istorija pg_dump bekapa cele baze (platformski, nezavisno od pgBackRest-a).
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Novo odredište
        </Button>
      </div>

      {data && data.destinations.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => setSelectedIds(e.target.checked ? destinationIds : [])}
              className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            />
            Izaberi sve
          </label>
          <span className="text-sm text-slate-400">
            {selectedIds.length > 0 ? `Izabrano: ${selectedIds.length}` : "Nijedno odredište nije izabrano"}
          </span>
          <Button
            size="sm"
            onClick={() => {
              setBulkRunResult(null);
              runSelected.mutate();
            }}
            disabled={selectedIds.length === 0 || runSelected.isPending}
          >
            <PlayCircle className="h-3.5 w-3.5" aria-hidden />
            Pokreni bekap sada za izabrana odredišta
          </Button>
          {bulkRunResult && <span className="text-sm text-slate-500">{bulkRunResult}</span>}
        </div>
      )}

      {data?.warnings && data.warnings.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            {data.warnings.map((w, i) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : !data || data.destinations.length === 0 ? (
        <EmptyState
          icon={ShieldAlert}
          title="Nema podešenih odredišta"
          description="Dodaj bar jedno odredište (po mogućstvu van produkcionog servera) da bi bekap krenuo po rasporedu."
        />
      ) : (
        <div className="space-y-4">
          {data.destinations.map((d) => (
            <DestinationCard
              key={d.id}
              destination={d}
              selected={selectedIds.includes(d.id)}
              onToggleSelected={toggleSelected}
            />
          ))}
        </div>
      )}

      <RestorePanel />

      {modalOpen && <DestinationFormModal initial={null} onClose={() => setModalOpen(false)} />}
    </div>
  );
}
